// frontend/src/components/NotificationBell.jsx
//
// Bell icon for the AppBar. Shows unread count, opens a dropdown with the
// most recent notifications, polls the backend every 30s, and surfaces a
// brief toast when a brand-new notification arrives.
//
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IconButton, Badge, Popover, List, ListItem, ListItemText, Typography,
  Box, Divider, Button, Tooltip, Snackbar, Alert, ListItemIcon
} from '@mui/material';
import {
  Notifications as BellIcon,
  CheckCircleOutline as ApprovedIcon,
  CancelOutlined as RejectedIcon,
  HourglassEmpty as PendingIcon,
  NotificationsNone as DefaultIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import notificationAPI from '../api/notificationAPI';

const POLL_MS = 30_000;

const iconForType = (type) => {
  if (type === 'quotation_approved')  return <ApprovedIcon color="success" />;
  if (type === 'quotation_rejected')  return <RejectedIcon color="error" />;
  if (type === 'quotation_submitted') return <PendingIcon color="warning" />;
  return <DefaultIcon color="action" />;
};

const fmtTimeAgo = (ts) => {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const lastSeenIdRef = useRef(null); // track most-recent ID we've already toasted

  const refresh = useCallback(async () => {
    try {
      const [list, unreadCount] = await Promise.all([
        notificationAPI.list({ limit: 30 }),
        notificationAPI.unreadCount()
      ]);
      setItems(list || []);
      setUnread(unreadCount || 0);

      // Toast any new unread items since last poll
      if (list && list.length > 0) {
        const newest = list[0];
        if (lastSeenIdRef.current === null) {
          lastSeenIdRef.current = newest.id; // first load — don't toast existing
        } else if (newest.id > lastSeenIdRef.current && !newest.is_read) {
          setToast({
            open: true,
            message: newest.title + (newest.message ? ' — ' + newest.message : ''),
            severity:
              newest.type === 'quotation_approved' ? 'success' :
              newest.type === 'quotation_rejected' ? 'error' : 'info'
          });
          lastSeenIdRef.current = newest.id;
        }
      }
    } catch (e) {
      // silent — bell shouldn't bother the user with API errors
      console.warn('NotificationBell refresh failed:', e?.message);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const handleClick = (e) => { setAnchorEl(e.currentTarget); refresh(); };
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (n) => {
    try {
      if (!n.is_read) await notificationAPI.markRead(n.id);
    } catch (_) { /* ignore */ }
    handleClose();
    if (n.link) navigate(n.link);
    refresh();
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      refresh();
    } catch (_) { /* ignore */ }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleClick} size="large">
          <Badge badgeContent={unread} color="error" max={99}>
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Notifications
          </Typography>
          {items.some(i => !i.is_read) && (
            <Button size="small" onClick={handleMarkAllRead}>Mark all read</Button>
          )}
        </Box>
        <Divider />
        <List dense disablePadding>
          {items.length === 0 && (
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2" color="text.secondary" align="center">No notifications yet</Typography>}
              />
            </ListItem>
          )}
          {items.map(n => (
            <ListItem
              key={n.id}
              button
              onClick={() => handleItemClick(n)}
              sx={{
                borderLeft: n.is_read ? 'none' : '3px solid',
                borderColor: 'primary.main',
                bgcolor: n.is_read ? 'transparent' : 'action.hover',
                alignItems: 'flex-start'
              }}
            >
              <ListItemIcon sx={{ mt: 0.5, minWidth: 36 }}>
                {iconForType(n.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: n.is_read ? 400 : 600 }}>
                    {n.title}
                  </Typography>
                }
                secondary={
                  <>
                    {n.message && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {n.message}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {fmtTimeAgo(n.created_at)}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </Popover>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
